<template>
	<div class="main-wrapper">
		<!-- Header -->
		<header class="d-flex justify-content-between">
			<h4 class="mb-4">User info</h4>
			<div class="typo-body-2 color-black-45">
				<span>User group: </span>
				<strong>{{ isLoading ? '...loading' : userInfo.group }}</strong>
			</div>
		</header>

		<!-- Approval Section -->
		<section class="d-flex justify-content-between align-items-baseline mb-4">
			<template v-if="approved">
				<label class="typo-label mr-1">Approved Account:</label>
				<CBadge class="badge-status" :color="APPROVED_STATUS[approved].class">
					{{ APPROVED_STATUS[approved].title }}
				</CBadge>
			</template>

			<template v-if="isShowMarkAsContent">
				<div class="right">
					<button
						class="btn btn-reject"
						color="secondary"
						data-test-id="button-reject"
						:disabled="edit.isUpdating"
						@click="onClickedReject"
					>
						Rejected
					</button>
					<button
						class="btn btn-secondary btn-approve"
						color="secondary"
						data-test-id="button-approve"
						:disabled="edit.isUpdating"
						@click="onClickedApprove"
					>
						Approved
					</button>
				</div>
			</template>
		</section>

		<!-- Loading -->
		<BaseLoading v-if="isLoading" is-fullpage />

		<!-- Form -->
		<CForm
			v-else
			class="main-wrapper"
			@submit.prevent="submit"
		>
			<CRow align-horizontal="center">
				<CCol lg="6">
					<CInput
						v-model.lazy.trim="staffId"
						:label="$t('page.users.create.formLabel.staffId')"
						disabled
					/>
				</CCol>
				<CCol lg="6">
					<CInput
						v-model.lazy.trim="$v.username.$model"
						:label="$t('page.users.create.formLabel.username')"
						:is-valid="isValidUsername"
						:invalid-feedback="getValidateUsernameMessage"
						disabled
					/>
				</CCol>
			</CRow>
			<CRow align-horizontal="center">
				<CCol lg="6">
					<CInput
						v-model.lazy.trim="$v.firstName.$model"
						:label="`${$t('page.users.create.formLabel.firstName')}*`"
						:is-valid="isValidFirstName"
						:invalid-feedback="getValidateFirstNameMessage"
						@input="clearError"
					/>
				</CCol>
				<CCol lg="6">
					<CInput
						v-model.lazy.trim="$v.lastName.$model"
						:label="$t('page.users.create.formLabel.lastName')"
						:is-valid="isValidLastName"
						:invalid-feedback="getValidateLastNameMessage"
						@input="clearError"
					/>
				</CCol>
			</CRow>
			<CRow align-horizontal="center">
				<CCol lg="6">
					<CInput
						v-model.lazy.trim="provider"
						:label="$t('page.users.create.formLabel.provider')"
						disabled
					/>
				</CCol>
				<CCol lg="6">
					<BaseDropDown
						v-model="$v.group.$model"
						:options="roleOptions"
						label="name"
						:label-drop-down="`${$t('page.users.create.formLabel.group')}*`"
						:searchable="false"
						:is-valid="!$v.group.$error"
						:invalid-feedback="$t('global.error.required')"
						track-by="value"
						placeholder="Please select group"
					/>
				</CCol>
			</CRow>
			<CRow align-horizontal="center">
				<CCol
					class="wrapper-active-field mt-4 mb-4"
					lg="12"
				>
					<label class="switch-active-label">
						{{
							$t('page.users.create.formLabel.status')
						}}
					</label>
					<CSwitch
						:checked.sync="status"
						class="switch-custom swtich-active-status-button"
						name="status"
						variant="3d"
						size="sm"
						color="success"
						@update:checked="handleClickSwitch"
					/>
					<p class="label-active-status typo-body-2">
						{{ getStatusLabel }}
					</p>
				</CCol>
			</CRow>
			<CRow align-horizontal="center">
				<CCol lg="12">
					<CInput
						v-if="isAllowChangePassword"
						v-model.lazy.trim="$v.password.$model"
						:label="$t('page.users.create.formLabel.password')"
						:is-valid="isValidPassword"
						:invalid-feedback="getValidatePasswordMessage"
						:description="getPasswordDescription"
						@input="clearError"
					/>
					<template v-else>
						<div v-if="!isSocialProvider" class="form-group">
							<label>Password</label>
							<div>
								<CButton
									class="btn-change-password flex transparent btn-export-csv"
									color="tertiary"
									@click="handleAllowChangePassword"
								>
									Change password
								</CButton>
							</div>
						</div>
					</template>
				</CCol>
			</CRow>
			<CRow align-horizontal="center">
				<CCol lg="12">
					<CInput
						v-model.lazy.trim="email"
						:label="$t('page.users.create.formLabel.email')"
						disabled
						@input="clearError"
					/>
				</CCol>
			</CRow>
			<CRow align-horizontal="center">
				<CCol lg="12">
					<CInput
						v-model.lazy.trim="$v.phone.$model"
						:label="$t('page.users.create.formLabel.phone')"
						:is-valid="isValidPhone"
						:invalid-feedback="getValidatePhoneMessage"
						@input="clearError"
					/>
				</CCol>
			</CRow>
			<CRow align-horizontal="center">
				<CCol lg="12">
					<BaseDropDown
						v-model="store"
						:options="getWarehouseOptions"
						label="name"
						label-drop-down="Store ID"
						placeholder="Select store"
					/>
				</CCol>
			</CRow>
			<CRow align-horizontal="center" class="mb-5">
				<CCol lg="12">
					<CTextarea
						v-model.lazy.trim="$v.note.$model"
						:label="$t('page.users.create.formLabel.note')"
						:is-valid="isValidNote"
						:invalid-feedback="getValidateNoteMessage"
						@input="clearError"
					/>
				</CCol>
			</CRow>
		</CForm>

		<!-- Action Buttons -->
		<BaseActionPanelStickyFooter
			:disabled-confirm="isLoading || isUpdating"
			content-class="main-wrapper"
			is-edit
			@onConfirm="submit"
			@onCancel="$router.push({ name: 'Users'})"
		/>

		<!-- Modals -->
		<BaseModalConfirm
			ref="modal-reject"
			title="Confirm"
			:description="`Do you want to 'Reject' account ${staffId}? If you confirm, this account will be deleted. If you want to verify the account again, please login withIT EASY again.`"
			@onConfirm="handleConfirm(REGISTER_STATUS.REJECTED)"
		/>

		<BaseModalConfirm
			ref="modal-approve"
			title="Confirm"
			:description="`Do you want to 'Approve' account ${staffId}?`"
			@onConfirm="handleConfirm(REGISTER_STATUS.APPROVED)"
		/>
	</div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import BaseModalConfirm from '@/components/BaseModalConfirm.vue';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import { validateHandler, checkIfValid } from '../assets/js/selectors/global/validation';
import { scrollToTop } from '../assets/js/helpers';
import { APPROVED_STATUS } from '../enums/users';
import { ROUTE_NAME } from '../enums/route';
import { SOCIAL_PROVIDER_GROUP_LIST, REGISTER_STATUS } from '../enums/social';
import { ROUTE_PERMISSION_KEY } from '../enums/permissions';

export default {
	name: 'UserEdit',
	components: {
		BaseModalConfirm,
	},
	validations: {
		username: {
			maxLength: maxLength(255),
		},
		password: {
			required(value) {
				if (this.isAllowChangePassword) {
					return required(value);
				}

				return true;
			},
			minLength: minLength(8),
			maxLength: maxLength(255),
		},
		firstName: {
			required,
			maxLength: maxLength(35),

		},
		lastName: {
			maxLength: maxLength(35),
		},
		phone: {
			maxLength: maxLength(255),
		},
		note: {
			maxLength: maxLength(150),
		},
		group: {
			required,
		},
	},
	mixins: [validationMixin],
	data() {
		return {
			APPROVED_STATUS,
			REGISTER_STATUS,

			isLoading: false,

			activeLabel: this.$t('page.users.create.value.status.inactive'),
			staffId: null,
			username: null,
			password: null,
			firstName: null,
			lastName: null,
			email: null,
			phone: null,
			store: null,
			note: null,
			provider: null,
			approved: null,
			group: null,
			status: null,
			sumitCount: 0,
			isAllowChangePassword: false,
		};
	},
	computed: {
		...mapGetters({
			userInfo: 'users/userInfo',
			warehouseOptions: 'warehouses/warehouseOptions',
			roleOptions: 'roles/roleOptions',
		}),
		...mapState('users', {
			edit: 'edit',
		}),
		...mapState('profile', {
			permissions: 'permissions',
		}),

		fullName() {
			const firstName = this.userInfo.firstName;
			const lastName = this.userInfo.lastName;

			return `${firstName}${lastName ? ` ${lastName}` : ''}`;
		},
		isUpdating() {
			return this.edit.isUpdating;
		},
		isSuccess() {
			return this.edit.isSuccess;
		},
		fieldErrors() {
			return this.edit.fieldErrors;
		},
		isValidUsername() {
			const field = this.$v.username;
			return checkIfValid(field);
		},
		isValidPassword() {
			const field = this.$v.password;
			return checkIfValid(field);
		},
		isValidPhone() {
			const field = this.$v.phone;
			return checkIfValid(field);
		},
		isValidFirstName() {
			const field = this.$v.firstName;
			return checkIfValid(field);
		},
		isValidLastName() {
			const field = this.$v.lastName;
			return checkIfValid(field);
		},
		isValidNote() {
			const field = this.$v.note;
			return checkIfValid(field);
		},
		getValidateUsernameMessage() {
			const field = this.$v.username;
			return validateHandler(field, {
				existMessage: this.$t('page.users.create.validationMessage.username.exists'),
				requiredMessage: this.$t('page.users.create.validationMessage.username.required'),
				lengthLimitMessage: this.$t('page.users.create.validationMessage.username.lengthLimit'),
			});
		},
		getValidatePasswordMessage() {
			const field = this.$v.password;

			return validateHandler(field, {
				requiredMessage: this.$t('page.users.create.validationMessage.password.required'),
				lengthLimitMessage: this.$t('page.users.create.validationMessage.password.lengthLimit'),
			});
		},
		getValidatePhoneMessage() {
			const field = this.$v.phone;
			return validateHandler(field, {
				lengthLimitMessage: this.$t('page.users.create.validationMessage.phone.lengthLimit'),
			});
		},
		getValidateFirstNameMessage() {
			const field = this.$v.firstName;

			return validateHandler(field, {
				requiredMessage: this.$t('page.users.create.validationMessage.firstName.required'),
				lengthLimitMessage: this.$t('page.users.create.validationMessage.firstName.lengthLimit'),
			});
		},
		getValidateLastNameMessage() {
			const field = this.$v.lastName;
			return validateHandler(field, {
				lengthLimitMessage: this.$t('page.users.create.validationMessage.lastName.lengthLimit'),
			});
		},
		getValidateNoteMessage() {
			const field = this.$v.note;
			return validateHandler(field, {
				lengthLimitMessage: this.$t('page.users.create.validationMessage.note.lengthLimit'),
			});
		},
		getPasswordDescription() {
			const field = this.$v.password;

			if (field.$error) {
				return '';
			}
			return this.$t('page.users.create.description.password');
		},
		formValid() {
			return !this.$v.$invalid;
		},
		getStatusLabel() {
			if (this.status) {
				return this.$t('page.users.create.value.status.active');
			}
			return this.$t('page.users.create.value.status.inactive');
		},
		isSocialProvider() {
			return SOCIAL_PROVIDER_GROUP_LIST.map((item) => item.provider).includes(this.provider);
		},
		isShowMarkAsContent() {
			const permission = this.permissions.find((permisison) => permisison.key === ROUTE_PERMISSION_KEY.USER);

			return this.approved === REGISTER_STATUS.PENDING && permission && permission.permissions.manage;
		},
		getWarehouseOptions() {
			return [
				{ com7BranchId: null, name: 'ไม่ระบุ', value: null },
				...this.warehouseOptions,
			];
		},
	},
	async created() {
		const userId = this.$route.params.id;
		this.isLoading = true;

		await Promise.all([
			this.getUser(userId),
			this.getRoleList({ per_page: 'all' }),
			this.getWarehouseList({ per_page: 'list' }),
		]);

		this.isLoading = false;

		const storeValue = this.getWarehouseOptions.find((warehouse) => warehouse.value === this.userInfo.storeId);
		const groupValue = this.roleOptions.find((role) => role.name === this.userInfo.group);

		// Update Page title
		this.updatePageTitle();

		// Set form data
		this.staffId = this.userInfo.staffId;
		this.username = this.userInfo.username;
		this.email = this.userInfo.email;
		this.phone = this.userInfo.phoneNumber;
		this.password = this.userInfo.password;
		this.firstName = this.userInfo.firstName;
		this.lastName = this.userInfo.lastName;
		this.status = this.userInfo.status;
		this.store = storeValue || null;
		this.note = this.userInfo.note;
		this.approved = this.userInfo.approved;
		this.provider = this.userInfo.provider;
		this.group = groupValue || null;
	},
	mounted() {
		scrollToTop();
	},
	methods: {
		...mapActions({
			setPageTitle: 'pageTitle/setPageTitle',
			getUser: 'users/getUser',
			updateUser: 'users/updateUser',
			clearError: 'users/clearError',
			updateMarkAs: 'users/updateMarkAs',
			showToast: 'toast/showToast',
			getWarehouseList: 'warehouses/getWarehouseList',
			getRoleList: 'roles/getRoleList',
		}),

		async submit() {
			this.$v.$touch();
			if (this.formValid) {
				const userDataStore = {
					name: this.username,
					password: this.password,
					first_name: this.firstName,
					last_name: this.lastName,
					is_active: this.status,
					phone_number: this.phone,
					store_id: this.store?.value || null,
					role_id: this.group?.value || null,
					note: this.note,
				};
				const userId = this.$route.params.id;

				await this.updateUser({
					userId,
					userDataStore,
				});

				if (this.isSuccess) {
					this.showToast({
						content: this.$t('global.successMessage'),
						header: this.$t('global.successMessageTitle'),
						type: 'success',
					});

					this.updatePageTitle();
				} else {
					let errorMessage = this.$t('global.errorMessage');

					if (this.fieldErrors.username) {
						errorMessage = this.fieldErrors.username;
					}

					this.showToast({
						content: errorMessage,
						header: this.$t('global.errorMessageTitleEdit'),
						type: 'danger',
					});

					scrollToTop();
				}
			} else {
				scrollToTop();
			}
		},
		handleAllowChangePassword() {
			this.isAllowChangePassword = true;
		},
		handleSelectGroup(value) {
			this.clearError();
			this.group = value;
		},
		handleClickSwitch() {
			this.clearError();
		},
		updatePageTitle() {
			this.setPageTitle({
				title: this.fullName,
				isShow: true,
			});
		},
		onClickedReject() {
			this.$refs['modal-reject'].open();
		},
		onClickedApprove() {
			this.$refs['modal-approve'].open();
		},
		async handleConfirm(status) {
			if (status === REGISTER_STATUS.APPROVED) {
				this.$v.$touch();

				if (this.$v.$invalid) {
					return;
				}
			}

			await this.updateMarkAs({ userId: this.$route.params.id, status });
			await this.$router.push({ name: ROUTE_NAME.USER_LISTS });
		},
	},
};
</script>
<style lang="scss" scoped>
.btn-reject {
	border: 1px solid #ddd !important;
	border-radius: 4px;
	margin-right: 16px;
	transition: all 0.3s;

	&:hover {
		border-color: #d0d0d0 !important;
	}
}

.btn-cancel,
.btn-create {
	min-width: 132px;
}

.btn-cancel {
	margin-left: 34px;
}

.btn-change-password {
	min-width: 155px;
}

.wrapper-active-field {
	display: flex;
	align-items: center;

	.switch-active-label {
		margin-right: 40px;
		margin-bottom: 0;
	}

	.swtich-active-status-button {
		margin-right: 12px;
	}

	.label-active-status {
		margin-bottom: 0;
	}
}
</style>
